/**
 * NotificationsBell tab classifier — pure helpers.
 *
 * Buckets every notification into one of the three V3 inbox tabs
 * (Action needed / FYI / Mentions) plus per-tab filter + count
 * primitives. Implemented as pure functions so the bell UI stays
 * presentational and the bucketing is trivial to unit-test.
 *
 * Lives in its OWN module (not as a sibling export on `NotificationsBell.tsx`)
 * so React Fast Refresh stays clean — vite-plugin-react treats a
 * non-component sibling export as an HMR break, forcing a full
 * module reload on every edit. Splitting the value exports out is
 * the documented fix in the plugin's HMR contract.
 */

import type { Notification } from '../../../notificationsService';

export type NotificationTab = 'action-needed' | 'fyi' | 'mentions';

/** Pure: does the wire shape look like a direct mention? Matches the
 *  prior bell's heuristic (type-contains 'mention' OR payload carries a
 *  mentioned_user_id). */
export function isMention(n: Notification): boolean {
    const t = n.type.toLowerCase();
    if (t.includes('mention')) return true;
    const payload = (n.payload ?? {}) as Record<string, unknown>;
    return Boolean(payload.mentioned_user_id);
}

/** Pure: which V3 tab a notification belongs to. Mention takes
 *  precedence so an @-mention in a review-category event still surfaces
 *  on the Mentions tab where users expect it. */
export function classifyTab(n: Notification): NotificationTab {
    if (isMention(n)) return 'mentions';
    // Action needed: anything that demands the viewer act. review +
    // system carry that intent today; message / social / general all
    // belong in FYI. A new `NotificationCategory` value added on the
    // backend would land in FYI by default — exhaustiveness check
    // below makes that explicit so the next dev sees the policy.
    switch (n.category) {
        case 'review':
        case 'system':
            return 'action-needed';
        case 'message':
        case 'social':
        case 'general':
            return 'fyi';
        default: {
            // Compile-time exhaustiveness guard: if NotificationCategory
            // ever gains a new variant, TypeScript flags this line and
            // forces the maintainer to choose a bucket explicitly.
            // Runtime fallback is 'fyi' (informational by default).
            const _exhaustive: never = n.category;
            void _exhaustive;
            return 'fyi';
        }
    }
}

/** Pure: filter the notifications visible on the given tab. */
export function filterByTab(
    notifications: ReadonlyArray<Notification>,
    tab: NotificationTab,
): Notification[] {
    return notifications.filter((n) => classifyTab(n) === tab);
}

/** Pure: count breakdown across the three tabs for the tab-strip
 *  badges. */
export function countByTab(
    notifications: ReadonlyArray<Notification>,
): Record<NotificationTab, number> {
    const out: Record<NotificationTab, number> = {
        'action-needed': 0,
        fyi: 0,
        mentions: 0,
    };
    for (const n of notifications) {
        out[classifyTab(n)] += 1;
    }
    return out;
}
